import { Button } from '@efexteam/components';
import { RoleIds } from '@efexteam/permission-manager';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import useImmediateApiCall from '../../hooks/useImmediateApiCall';
import authSvc from '../../services/auth/authSvc';
import {
  IBankAccountDetails,
  ICompanyDetails,
  IFileInformation,
  IStakeholdersDetails,
  UserProfileResponse,
} from '../../services/auth/types';
import { useAppDispatch } from '../../store';
import { aesDecryptCBC } from '../../utils/crypt';
import BankAccountDetails from './BankAccountDetails';
import CompanyDetails from './CompanyDetails';
import CompanyFiles from './CompanyFiles';
import DeleteAccount from './DeleteAccount';
import StakeholdersDetails from './StakeholdersDetails';
import { fetchUserProfile } from './store';
import { selectProfileData, selectProfileState } from './store/selectors';

const MyProfile = () => {
  const { t } = useTranslation();
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] =
    useState(false);
  const [blockEdited, setBlockEdited] = useState<
    'company' | 'bank' | 'legalRep' | 'fileInfo' | 'stakeInfo'
  >();
  const userProfile = useSelector(selectProfileState);
  const userProfileRoles = useSelector(selectProfileData);
  const dispatch = useAppDispatch();

  const { data: profileInformation, loading } =
    useImmediateApiCall<UserProfileResponse>(() => authSvc.getUserProfile());

  const data = useMemo(
    () =>
      profileInformation && {
        ...profileInformation,
        company: {
          ...profileInformation.company,
          email: aesDecryptCBC(profileInformation.company.email || ''),
        },
        legalRepresentative: {
          ...profileInformation.legalRepresentative,
          email: aesDecryptCBC(
            profileInformation.legalRepresentative.email || ''
          ),
          phoneNumber: aesDecryptCBC(
            profileInformation.legalRepresentative.phoneNumber || ''
          ),
        },
        bankAccount: {
          ...profileInformation.bankAccount,
          clabe: aesDecryptCBC(profileInformation.bankAccount.clabe || ''),
          accountNumber: aesDecryptCBC(
            profileInformation.bankAccount.accountNumber || ''
          ),
          routingNumber: aesDecryptCBC(
            profileInformation.bankAccount.routingNumber || ''
          ),
        },
      },
    [profileInformation]
  );

  const {
    data: dataUpdated,
    loading: loadingData,
    invoke,
  } = useApiCall((form) => authSvc.updateProfileInformation(form));

  const handleSubmit = (
    userInformation:
      | ICompanyDetails
      | IBankAccountDetails
      | IStakeholdersDetails
      | IFileInformation,
    block: 'company' | 'bank' | 'legalRep' | 'fileInfo' | 'stakeInfo'
  ) => {
    setBlockEdited(block);
    invoke({
      ...userProfile.data?.legalRepresentative,
      ...userProfile.data?.bankAccount,
      ...userProfile.data?.company,
      ...userProfile.data?.stakeholders,
      ...userInformation,
    });
  };

  useEffect(() => {
    if (blockEdited) dispatch(fetchUserProfile());
  }, [blockEdited, dataUpdated, dispatch]);

  return (
    <>
      <CompanyDetails
        loading={loading || (loadingData && blockEdited === 'company')}
        company={
          blockEdited === 'company' ? userProfile.data?.company : data?.company
        }
        roleId={userProfile.data?.legalRepresentative.roleId}
        userType={userProfile.data?.userType}
        onSubmit={handleSubmit}
      />
      {!userProfileRoles?.isAwActive && (
        <BankAccountDetails
          loading={loading || (loadingData && blockEdited === 'bank')}
          bankAccount={
            blockEdited === 'bank'
              ? userProfile.data?.bankAccount
              : data?.bankAccount
          }
          company={
            blockEdited === 'company'
              ? userProfile.data?.company
              : data?.company
          }
          onSubmit={handleSubmit}
        />
      )}
      {Object.keys(
        blockEdited === 'fileInfo'
          ? userProfile.data?.files ?? {}
          : data?.files ?? {}
      ).length > 0 ? (
        <CompanyFiles
          loading={loading || (loadingData && blockEdited === 'fileInfo')}
          fileInfo={
            blockEdited === 'fileInfo' ? userProfile.data?.files : data?.files
          }
          company={
            blockEdited === 'company'
              ? userProfile.data?.company
              : data?.company
          }
          onSubmit={handleSubmit}
        />
      ) : null}
      {data &&
      data.userType &&
      data.userType === 'COMPANY' &&
      blockEdited === 'company'
        ? (userProfile.data?.stakeholders ?? []).length > 0
        : (data?.stakeholders ?? []).length > 0 && (
            <StakeholdersDetails
              loading={loading || (loadingData && blockEdited === 'stakeInfo')}
              stakeholder={
                blockEdited === 'company'
                  ? userProfile.data?.stakeholders
                  : data?.stakeholders
              }
            />
          )}

      {userProfileRoles?.legalRepresentative.roleId === RoleIds.OWNER ||
      userProfileRoles?.legalRepresentative.roleId ===
        RoleIds.PAYMENT_APPROVER ? (
        <div className="flex justify-start items-center gap-5 !my-3">
          <Button
            size="small"
            appearance="secondary"
            color="error"
            onClick={() => setIsDeleteAccountModalOpen(true)}
          >
            {t('deleteAccount') || ''}
          </Button>
        </div>
      ) : null}
      {isDeleteAccountModalOpen ? (
        <DeleteAccount
          open={isDeleteAccountModalOpen}
          close={setIsDeleteAccountModalOpen}
        />
      ) : null}
    </>
  );
};

export default MyProfile;
